import React, { useState, useEffect } from 'react';
import { TextField, IconButton, Button, Autocomplete, Box, Typography, Stack, Divider, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import API_BASE_URL from '../config';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';

// Styled components
const JobCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
    '&:hover': {
        boxShadow: `0 12px 40px 0 ${alpha(theme.palette.common.black, 0.12)}`,
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const InfoTypeIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
}));
const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    color: 'white',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
}));
const DeleteButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.error.main,
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        transform: 'scale(1.1)',
    },
}));

const AddJobDetail = ({ formData, editMode, setAllJobDetails }) => {
    const [jobDetails, setJobDetails] = useState([]);
    const [suggestions, setSuggestions] = useState([]);

    // Job type suggestions
    const jobTypeSuggestions = [
        'Full-Time', 'Part-Time', 'Freelance', 'Internship', 'Contract'
    ];

    const formDataId = formData?.id;

    // Fetch job details based on user_id when component mounts
    useEffect(() => {
        if (!formDataId) return;
        const fetchJobDetails = async () => {
            try {
                    const response = await axios.get(`${API_BASE_URL}/jobs/${formDataId}`, {
                        withCredentials: true
                    });
                    console.log('response-job', response.data);
                    if (response.data && Array.isArray(response.data)) {
                        if (response.data.length > 0) {
                            setJobDetails(response.data);
                        } else {
                            setJobDetails([{ id: 1, user_id: formDataId, company_name: '', position: '', start_year: '', end_year: '', current_job: false, job_type: '' }]);
                        }
                    } else {
                        console.error('Invalid response format:', response.data);
                        setJobDetails([{ id: 1, user_id: formDataId, company_name: '', position: '', start_year: '', end_year: '', current_job: false, job_type: '' }]);
                    }
            } catch (error) {
                    console.error('Error fetching job details:', error);
                    if (error.response?.status === 401) {
                        console.log('User not authenticated, redirecting to login...');
                    } else if (error.response?.status === 404) {
                        console.log('No job details found for user');
                    } else {
                        console.error('Error fetching job details:', error);
                    }
            }
        };
        fetchJobDetails();
    }, [formDataId]);



    const handleAddJob = () => {
        setJobDetails([
            ...jobDetails,
            {
                id: `temp_${Date.now()}`,
                user_id: formData.id || '',
                company_name: '',
                position: '',
                start_year: '',
                end_year: '',
                current_job: false,
                job_type: ''
            }
        ]);
    };

    const handleRemoveJob = async (index, jobId) => {
        const originalJobs = [...jobDetails];

        // If it's a temporary ID, just remove from UI and sync with parent
        if (!jobId || String(jobId).startsWith('temp')) {
            const newJobs = jobDetails.filter((_, i) => i !== index);
            const finalJobs = newJobs.length ? newJobs : [{ id: `temp_${Date.now()}`, user_id: formData.id, company_name: '', position: '', start_year: '', end_year: '', current_job: false, job_type: '' }];
            setJobDetails(finalJobs);
            setAllJobDetails(finalJobs);
            return;
        }

        // ✅ STEP 1: Optimistic UI Update
        const updatedJobs = jobDetails.filter((job) => job.id !== jobId);
        // If last row removed, add empty one
        const finalJobs = updatedJobs.length ? updatedJobs : [{ id: `temp_${Date.now()}`, user_id: formData.id, company_name: '', position: '', start_year: '', end_year: '', current_job: false, job_type: '' }];
        
        setJobDetails(finalJobs);
        setAllJobDetails(finalJobs);

        try {
            const response = await axios.delete(`${API_BASE_URL}/jobs/${jobId}`, {
                withCredentials: true
            });
            if (!response.data.success) {
                throw new Error(response.data.message || "Unable to delete item");
            }
        } catch (error) {
            // ✅ STEP 3: Revert both local and parent state on error
            setJobDetails(originalJobs);
            setAllJobDetails(originalJobs);

            const status = error.response?.status;
            let userMessage = "Unable to delete item";
            if (status === 404) userMessage = "Item already deleted or not found";
            else if (status === 500) userMessage = "Server error";
            alert(userMessage);
            console.error('Delete Job Error:', error);
        }
    };

    const handleFieldChange = (index, field, value) => {
        const newJobDetails = [...jobDetails];
        newJobDetails[index][field] = value;
        setJobDetails(newJobDetails);
        setAllJobDetails(newJobDetails); // Update parent component's state with the latest data
    };

    const handleSearch = async (index, field, value) => {
        if (value) {
            try {
                const response = await axios.get(`${API_BASE_URL}/search`, {
                    params: {
                        [field]: value
                    }
                });

                if (response.data.length > 0) {
                    setSuggestions(response.data);
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setSuggestions([]);
            }
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionSelect = (index, option) => {
        const newJobDetails = [...jobDetails];
        newJobDetails[index].company = option.company;
        setJobDetails(newJobDetails);
        setSuggestions([]); // Clear suggestions after selection
    };

    return (
        <Box>
            <Stack spacing={3}>
                {jobDetails.map((detail, index) => (
                    <JobCard key={index}>
                        <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                               <InfoTypeIcon  sx={{
                        ...(editMode ? {
                          color: (theme) => theme.palette.primary.main,
                          opacity: 1
                        } : {
                          color: 'text.secondary',
                          opacity: 0.6
                        })
                      }}>
                                <WorkIcon />
                                </InfoTypeIcon>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        ...(editMode ? {
                                            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            color: 'transparent',
                                        } : {
                                            color: 'text.secondary',
                                            opacity: 0.7
                                        })
                                    }}
                                >
                                    {index === 0 ? 'Current Job' : `Previous Job ${index}`}
                                </Typography>
                            </Stack>

                            <Divider />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <StyledAutocomplete
                                    freeSolo
                                    options={jobTypeSuggestions}
                                    value={detail.job_type || ''}
                                    inputValue={detail.job_type || ''}
                                    onInputChange={(event, newInputValue) => {
                                        handleFieldChange(index, 'job_type', newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <StyledTextField
                                            {...params}
                                            label="Job Type"
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    )}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledAutocomplete
                                    freeSolo
                                    options={suggestions.map((option) => option?.company_name || '')}
                                    value={detail.company_name || ''}
                                    inputValue={detail.company_name || ''}
                                    onInputChange={(event, newInputValue) => {
                                        handleFieldChange(index, 'company_name', newInputValue);
                                    }}
                                    onChange={(event, newValue) => {
                                        const selectedOption = suggestions.find(option => option.company_name === newValue);
                                        if (selectedOption) {
                                            handleSuggestionSelect(index, selectedOption);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <StyledTextField
                                            {...params}
                                            label="Company"
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    )}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledTextField
                                    label="Start Year"
                                    type="number"
                                    value={detail.start_year}
                                    onChange={(e) => handleFieldChange(index, 'start_year', e.target.value)}
                                    fullWidth
                                    disabled={!editMode}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledTextField
                                    label="End Year"
                                    type="number"
                                    value={detail.end_year}
                                    onChange={(e) => handleFieldChange(index, 'end_year', e.target.value)}
                                    fullWidth
                                    disabled={!editMode}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledTextField
    label="Work Done / Occupation"
    value={detail.work_done || ''}
    onChange={(e) => handleFieldChange(index, 'work_done', e.target.value)}
    fullWidth
    disabled={!editMode}
    multiline
    rows={1}
    sx={{ flex: 1, width: '100%' }}
/>

                                {index > 0 && (
                                    <DeleteButton
                                        onClick={() => handleRemoveJob(index, detail.id)}
                                        disabled={!editMode}
                                    >
                                        <DeleteIcon />
                                    </DeleteButton>
                                )}
                            </Stack>
                        </Stack>
                    </JobCard>
                ))}

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 2
                }}>
                     {editMode && (
                    <ActionButton
                        onClick={handleAddJob}
                        disabled={!editMode}
                        startIcon={<AddIcon />}
                    >
                        Add Job Detail
                    </ActionButton> )}
                </Box>
            </Stack>
        </Box>
    );
};

export default AddJobDetail;
